import React, { useCallback, useEffect, useState } from "react"
import clsx from "clsx"

import { useTranslation } from "react-i18next"

import { SingleOptTimeRangePicker } from "components/date/SingleOptTimePeriodPicker"
import { LoadingIndicator } from "components/general/LoadingIndicator"
import { StatCard } from "components/stats/Card"

import { useProps } from "contexts/PropsContext"

import { AdminBonus2Service } from "services/bonus/AdminBonus2Service"
import { BonusAnalyticsResponse } from "services/bonus/type"

import { getSummaryStats, getWageringStats } from "./analyticsHelpers"
import { BonusTypeCard } from "./BonusTypeCard"
import { DailyActivationsChart } from "./DailyActivationsChart"
import { FreeSpinsSummaryCard } from "./FreeSpinsSummaryCard"
import { StatusBreakdownCard } from "./StatusBreakdownCard"
import { TopPerformingBonuses } from "./TopPerformingBonuses"

interface Props {
  tenant: string
}

export const BonusAnalytics = ({ tenant }: Props) => {
  const { t } = useTranslation("translation", { useSuspense: false })
  const { darkMode } = useProps()
  const [analytics, setAnalytics] = useState<BonusAnalyticsResponse | null>(null)
  const [loading, setLoading] = useState(false)
  const [timeframe, setTimeframe] = useState<(Date | null)[]>([null, null])

  const loadAnalytics = useCallback(async () => {
    if (!tenant) return

    const startDate = timeframe[0]?.toISOString().split("T")[0]
    const endDate = timeframe[1]?.toISOString().split("T")[0]

    setLoading(true)
    // Iteration-3 Fix 02 — the service returns undefined for BOTH a real failure and a
    // tenant with no bonus activity. Do NOT fire an error toast on falsy data (that made an
    // empty-but-healthy tenant look broken). The render shows a friendly empty-state instead;
    // genuine transport errors are already surfaced by the service's parseError.
    const data = await AdminBonus2Service.getAnalytics(tenant, startDate, endDate)
    setAnalytics(data ?? null)
    setLoading(false)
  }, [tenant, timeframe])

  useEffect(() => {
    loadAnalytics()
  }, [loadAnalytics])

  const handleTimeframeChange = useCallback((dates: Date[]) => {
    setTimeframe(dates)
  }, [])

  if (loading) {
    return <LoadingIndicator text={t("bonus2.analytics.loading") as string} />
  }

  if (!analytics) {
    // Fix 02 — friendly empty-state: an empty tenant is not an error.
    return (
      <div className={clsx("text-center py-12", darkMode ? "text-gray-400" : "text-gray-600")}>
        {t("bonus2.analytics.noBonusActivityYet")}
      </div>
    )
  }

  const { summary, statusCounts, byType, dailyActivations, bonusPerformance, freeSpinsStats, period } = analytics

  const summaryStats = getSummaryStats(summary)
  const wageringStats = getWageringStats(summary)

  return (
    <div className="space-y-6">
      <div
        className={clsx(
          "flex flex-wrap gap-6 pb-2 gap-y-3 justify-start max-w-full w-full border-b md:flex-row md:gap-6 md:items-end md:pb-4",
          darkMode ? "border-[rgba(255,255,255,0.1)]" : "border-[rgba(0,0,0,0.05)]"
        )}
      >
        <div className="max-w-md">
          <SingleOptTimeRangePicker noLabel onTimeframeChange={handleTimeframeChange} extendedPresetOptions={true} />
        </div>
      </div>

      <div className="grid grid-cols-[repeat(auto-fit,_minmax(200px,_1fr))] gap-5 w-full">
        {summaryStats.map((stat) => (
          <StatCard
            key={stat.title}
            title={t(stat.title)}
            value={stat.value}
            loading={loading}
            type={stat.isCurrency ? "currency" : undefined}
          />
        ))}
      </div>

      <div className="grid grid-cols-[repeat(auto-fit,_minmax(200px,_1fr))] gap-5 w-full">
        {wageringStats.map((stat) => (
          <StatCard
            key={stat.title}
            title={t(stat.title)}
            value={stat.value}
            loading={loading}
            type={stat.isCurrency ? "currency" : undefined}
          />
        ))}
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <StatusBreakdownCard statusCounts={statusCounts} totalPlayerBonuses={summary.totalPlayerBonuses} />
        <BonusTypeCard byType={byType} />
      </div>

      <DailyActivationsChart dailyActivations={dailyActivations} />
      <TopPerformingBonuses bonusPerformance={bonusPerformance} />
      <FreeSpinsSummaryCard freeSpinsStats={freeSpinsStats} loading={loading} />

      <div className={clsx("text-center text-sm", darkMode ? "text-gray-500" : "text-gray-400")}>
        Data from {new Date(period.startDate).toLocaleDateString()} to {new Date(period.endDate).toLocaleDateString()}
      </div>
    </div>
  )
}
